import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, Users, Ticket, Building2, Clock, Image, Hash } from 'lucide-react';
import type { PromoOffer } from '../../types';

interface PromoOfferDetailsProps {
  promoOffer: PromoOffer;
}

export function PromoOfferDetails({ promoOffer }: PromoOfferDetailsProps) {
  const isExpired = new Date(promoOffer.expires_at) < new Date();
  
  return (
    <div className="space-y-3">
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardContent className="py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded bg-primary/10 p-1">
                <Hash className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Promo Code</p>
                <p className="text-xl font-mono font-bold tracking-wide text-primary">
                  {promoOffer.promo_code}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge 
                variant={promoOffer.is_active ? 'default' : 'secondary'}
                className="px-2 py-0.5 text-xs"
              >
                {promoOffer.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {isExpired && (
                <Badge variant="destructive" className="px-2 py-0.5 text-xs">
                  Expired
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {promoOffer.media_url && (
        <Card>
          <CardHeader className="pb-1 pt-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Image className="h-3.5 w-3.5 text-muted-foreground" />
              Media
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2">
            <div className="max-w-md mx-auto">
              <img 
                src={promoOffer.media_url} 
                alt="Promo offer media" 
                className="w-full rounded shadow-sm hover:shadow-md transition-shadow"
              />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            Venue Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="flex justify-between items-center py-1 border-b">
            <span className="text-xs text-muted-foreground">Venue</span>
            <span className="text-sm font-medium">{promoOffer.venue_name}</span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-xs text-muted-foreground">Organization</span>
            <span className="text-sm font-medium">{promoOffer.organization_name}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Ticket className="h-3.5 w-3.5 text-muted-foreground" />
            Offer Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          {promoOffer.brand && (
            <div className="flex justify-between items-center py-1 border-b">
              <span className="text-xs text-muted-foreground">Brand</span>
              <span className="text-sm font-medium">{promoOffer.brand}</span>
            </div>
          )}
          <div className="py-1">
            <p className="text-xs text-muted-foreground mb-1">Short Description</p>
            <p className="text-sm font-medium">{promoOffer.short_description}</p>
          </div>
          {promoOffer.detailed_description && (
            <div className="py-1.5 border-t">
              <p className="text-xs text-muted-foreground mb-1">Detailed Description</p>
              <p className="text-xs whitespace-pre-wrap">{promoOffer.detailed_description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            Claim Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-blue-50 dark:bg-blue-950/30 p-2 rounded border border-blue-200 dark:border-blue-900">
              <p className="text-[10px] font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                Active Claims
              </p>
              <p className="text-lg font-bold">{promoOffer.active_claims}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-950/30 p-2 rounded border border-green-200 dark:border-green-900">
              <p className="text-[10px] font-medium text-green-600 dark:text-green-400 mb-0.5">
                Redeemed
              </p>
              <p className="text-lg font-bold">{promoOffer.redeemed_claims}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-950/30 p-2 rounded border border-purple-200 dark:border-purple-900">
              <p className="text-[10px] font-medium text-purple-600 dark:text-purple-400 mb-0.5">
                Total Claimed
              </p>
              <p className="text-lg font-bold">{promoOffer.claimed_count}</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-950/30 p-2 rounded border border-orange-200 dark:border-orange-900">
              <p className="text-[10px] font-medium text-orange-600 dark:text-orange-400 mb-0.5">
                Max Total
              </p>
              <p className="text-lg font-bold">{promoOffer.max_claim_total}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
            Claim Limits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="flex justify-between items-center py-1 border-b">
            <span className="text-xs text-muted-foreground">Max per Person</span>
            <Badge variant="outline" className="font-mono text-xs px-2 py-0">
              {promoOffer.max_claim_per_person}
            </Badge>
          </div>
          <div className="py-1">
            <p className="text-xs text-muted-foreground mb-0.5">Expires At</p>
            <p className={`text-sm font-medium ${isExpired ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
              {format(new Date(promoOffer.expires_at), 'PPpp')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            Timestamps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="flex justify-between items-center py-1 border-b">
            <span className="text-xs text-muted-foreground">Created</span>
            <span className="text-xs font-medium">
              {format(new Date(promoOffer.created_at), 'PP')}
            </span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-xs text-muted-foreground">Last Updated</span>
            <span className="text-xs font-medium">
              {format(new Date(promoOffer.updated_at), 'PP')}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}